import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Select, Textarea, Toggle } from '../../components/ui';
import { useListaVehiculos, useListaOperadores, useListaMaquinas } from '../../hooks/useReferencias';
import type { OrdenCargaCombustibleSolicitarRequest } from '../../types/ordenesCargaCombustible';

interface FormularioSolicitudCargaProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (datos: OrdenCargaCombustibleSolicitarRequest) => void;
  isLoading?: boolean;
}

export const FormularioSolicitudCarga: React.FC<FormularioSolicitudCargaProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [incluirVehiculo, setIncluirVehiculo] = useState(true);
  const [incluirMaquina, setIncluirMaquina] = useState(false);
  const [operadorId, setOperadorId] = useState<number | ''>('');
  const [vehiculoId, setVehiculoId] = useState<number | ''>('');
  const [maquinaId, setMaquinaId] = useState<number | ''>('');
  const [cantidadVehiculo, setCantidadVehiculo] = useState('');
  const [cantidadMaquina, setCantidadMaquina] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const { data: vehiculos } = useListaVehiculos();
  const { data: maquinas } = useListaMaquinas();
  const { data: operadores } = useListaOperadores();

  const resetear = () => {
    setIncluirVehiculo(true);
    setIncluirMaquina(false);
    setOperadorId('');
    setVehiculoId('');
    setMaquinaId('');
    setCantidadVehiculo('');
    setCantidadMaquina('');
    setObservaciones('');
  };

  const handleClose = () => {
    resetear();
    onClose();
  };

  const esValido = !!operadorId && (incluirVehiculo || incluirMaquina) && (
    (!incluirVehiculo || !!vehiculoId) &&
    (!incluirMaquina || !!maquinaId)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!esValido || !operadorId) return;

    onSubmit({
      operador_id: Number(operadorId),
      destinos: [
        ...(incluirVehiculo ? [{
          vehiculo_id: Number(vehiculoId),
          cantidad_solicitada: cantidadVehiculo ? Number(cantidadVehiculo) : undefined,
        }] : []),
        ...(incluirMaquina ? [{
          maquina_id: Number(maquinaId),
          cantidad_solicitada: cantidadMaquina ? Number(cantidadMaquina) : undefined,
        }] : []),
      ],
      observaciones_solicitud: observaciones || undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('ordenes_carga.solicitar.title')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label={t('ordenes_carga.form.operador')}
          value={operadorId}
          onChange={(e) => setOperadorId(e.target.value ? Number(e.target.value) : '')}
          options={[
            { value: '', label: t('common.select') },
            ...(operadores || []).map((o: any) => ({ value: o.id, label: `${o.codigo} - ${o.descripcion}` })),
          ]}
          required
        />

        <div className="border border-gray-200 rounded-md p-3 space-y-3">
          <Toggle checked={incluirVehiculo} onChange={setIncluirVehiculo} label={t('ordenes_carga.solicitar.incluir_vehiculo')} />
          {incluirVehiculo && (
            <div className="space-y-3 pt-1">
              <Select
                label={t('ordenes_carga.form.vehiculo')}
                value={vehiculoId}
                onChange={(e) => setVehiculoId(e.target.value ? Number(e.target.value) : '')}
                options={[
                  { value: '', label: t('common.select') },
                  ...(vehiculos || []).map(v => ({ value: v.id, label: `${v.codigo} - ${v.patente}` })),
                ]}
                required
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.solicitar.cantidad_deseada')}
                value={cantidadVehiculo}
                onChange={(e) => setCantidadVehiculo(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-md p-3 space-y-3">
          <Toggle checked={incluirMaquina} onChange={setIncluirMaquina} label={t('ordenes_carga.solicitar.incluir_maquina')} />
          {incluirMaquina && (
            <div className="space-y-3 pt-1">
              <Select
                label={t('ordenes_carga.table.maquina')}
                value={maquinaId}
                onChange={(e) => setMaquinaId(e.target.value ? Number(e.target.value) : '')}
                options={[
                  { value: '', label: t('common.select') },
                  ...(maquinas || []).map((m: any) => ({ value: m.id, label: `${m.codigo} - ${m.descripcion || ''}` })),
                ]}
                required
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.solicitar.cantidad_deseada')}
                value={cantidadMaquina}
                onChange={(e) => setCantidadMaquina(e.target.value)}
              />
            </div>
          )}
        </div>

        <Textarea
          label={t('ordenes_carga.solicitar.observaciones')}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />

        <div className="flex justify-end space-x-3 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!esValido} loading={isLoading}>
            {t('ordenes_carga.solicitar.confirmar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FormularioSolicitudCarga;
